import React from 'react';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Container from 'components/Container';
import { FeatureKey, FeatureKeys, hasFeature } from './FeatureKeys';
import { useEntitlement } from './useEntitlement';

const C = {
  ink: '#17263d',
  muted: '#78869a',
  blue: '#1475d1',
};

const FEATURE_META: Record<
  FeatureKey,
  { label: string; minTier: string; description: string }
> = {
  [FeatureKeys.MONEY_SNAPSHOT]: {
    label: 'Spending snapshot',
    minTier: 'Free',
    description: 'Basic spending overview',
  },
  [FeatureKeys.MONEY_RATIO]: {
    label: 'Ratio breakdown',
    minTier: 'Free',
    description: '50/30/20 budget ratios',
  },
  [FeatureKeys.MONEY_TRANSACTIONS]: {
    label: 'Transactions',
    minTier: 'Free',
    description: 'Transaction history',
  },
  [FeatureKeys.MONEY_COACH]: {
    label: 'Money Coach',
    minTier: 'Plus',
    description: 'Personalised insights and action plans',
  },
  [FeatureKeys.MONEY_FORECAST]: {
    label: 'Spending forecast',
    minTier: 'Plus',
    description: 'Month-end projection and trajectory',
  },
  [FeatureKeys.MONEY_CSV_IMPORT]: {
    label: 'CSV import',
    minTier: 'Plus',
    description: 'Import transactions from your bank',
  },
  [FeatureKeys.GOALS_CORE]: {
    label: 'Goals',
    minTier: 'Plus',
    description: 'Create and track savings goals',
  },
  [FeatureKeys.HOUSEHOLD_CORE]: {
    label: 'Household',
    minTier: 'Family',
    description: 'Shared finances with your household',
  },
  [FeatureKeys.BUSINESS_CORE]: {
    label: 'Business workspace',
    minTier: 'Business',
    description: 'Business financial workspace',
  },
};

interface FeatureGateProps {
  featureKey: FeatureKey;
  children: React.ReactNode;
}

const FeatureGate = ({
  featureKey,
  children,
}: FeatureGateProps): JSX.Element => {
  const { info, loading } = useEntitlement();

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={300}
      >
        <CircularProgress />
      </Box>
    );
  }

  const enabled = info ? hasFeature(info.enabledFeatures, featureKey) : false;

  if (enabled) {
    return <>{children}</>;
  }

  const meta = FEATURE_META[featureKey];

  return (
    <Container paddingY={{ xs: 4, sm: 6 }}>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        textAlign="center"
        maxWidth={480}
        mx="auto"
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            bgcolor: alpha(C.blue, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 3,
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 32, color: C.blue }} />
        </Box>

        <Typography variant="h5" fontWeight={700} sx={{ color: C.ink, mb: 1 }}>
          {meta.label} is a {meta.minTier} feature
        </Typography>
        <Typography variant="body2" sx={{ color: C.muted, mb: 4 }}>
          {meta.description}. Upgrade to Docket {meta.minTier} to unlock this
          and all {meta.minTier} features.
        </Typography>

        <Card
          variant="outlined"
          sx={{
            width: '100%',
            borderRadius: 3,
            borderColor: alpha(C.blue, 0.3),
            mb: 3,
          }}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{ color: C.ink, mb: 1.5 }}
            >
              What's included in {meta.minTier}
            </Typography>
            {plusHighlights.map((item) => (
              <Box
                key={item}
                display="flex"
                alignItems="center"
                gap={1}
                mb={0.75}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: C.blue,
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2" sx={{ color: C.muted }}>
                  {item}
                </Typography>
              </Box>
            ))}
          </CardContent>
        </Card>

        <Box
          sx={{
            bgcolor: alpha(C.blue, 0.06),
            border: `1px solid ${alpha(C.blue, 0.2)}`,
            borderRadius: 2,
            px: 2.5,
            py: 1.5,
            width: '100%',
          }}
        >
          <Typography variant="body2" fontWeight={600} sx={{ color: C.ink }}>
            Subscribe via the Docket iOS app
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: C.muted, mt: 0.5, display: 'block' }}
          >
            Subscriptions are managed through the App Store. Open Docket on your
            iPhone to upgrade.
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

const plusHighlights = [
  'Full Money Coach insights and action plans',
  'Month-end spend forecast and trajectory',
  'Unlimited savings goals',
  'CSV import from any bank',
  'Monthly budget targets',
];

export default FeatureGate;
